import { BUILDING_NUMBER_REGEX, PHONE_VALIDATION_REGEX } from '@/consts';
import { validatorNip } from '@/helpers/nipValidator';
import { validatorRegon } from '@/helpers/regonValidator';
import { EnumLike } from '@/types';

import { ibanValidator } from './helpers/iban';
import { peselValidator } from './helpers/peselValidator';
import {
  check,
  custom,
  email,
  maxLength,
  minLength,
  nonEmpty,
  pipe,
  regex,
  safeParse,
  string,
} from 'valibot';

export const validateBankAccountNumber = (value: string) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij numer konta bankowego'),
      minLength(26, 'Numer bankowy powinien się składać z 26 cyfr'),
      maxLength(26, 'Numer bankowy powinien się składać z 26 cyfr'),
      check((value) => {
        return ibanValidator(value.replace(/\s/g, ''));
      }, 'Podaj prawidłowy numer konta bankowego')
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateCompanyName = (value: string) => {
  const result = safeParse(
    pipe(string('Uzupełnij nazwę spółki'), nonEmpty('Uzupełnij nazwę spółki')),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateFirstName = (value: string) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij imię'),
      nonEmpty('Uzupełnij imię'),
      minLength(2, 'Uzupełnij imię'),
      regex(/^[^\d]+$/, 'Imie jest niepoprawne')
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateLastName = (value: string) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij nazwisko'),
      nonEmpty('Uzupełnij nazwisko'),
      minLength(2, 'Uzupełnij nazwisko'),
      regex(/^[^\d]+$/, 'Nazwisko jest niepoprawne')
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validatePhone = ({
  value,
  otherPhones,
  accountPhone,
  isOptional = false,
}: {
  value: string;
  otherPhones: string[];
  accountPhone?: string | null;
  isOptional?: boolean;
}) => {
  if (isOptional && !value) {
    return true;
  }

  const result = safeParse(
    pipe(
      string('Uzupełnij numer telefonu'),
      nonEmpty('Uzupełnij numer telefonu'),
      regex(PHONE_VALIDATION_REGEX, 'Podaj prawidłowy numer telefonu'),
      check((value) => {
        return !otherPhones.includes(value);
      }, 'Numer telefonu jest już używany'),
      check((value) => {
        return value !== accountPhone;
      }, 'Wprowadź inny numer lub zaznacz powyższe pole, jeśli jesteś właścicielem')
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateEmail = ({
  value,
  otherMails,
  accountEmail,
  isOptional = false,
}: {
  value: string;
  otherMails: string[];
  accountEmail?: string | null;
  isOptional?: boolean;
}) => {
  if (isOptional && !value) {
    return true;
  }
  const result = safeParse(
    pipe(
      string('Uzupełnij e-mail'),
      nonEmpty('Uzupełnij e-mail'),
      email('Podaj prawidłowy adres e-mail'),
      check((value) => {
        return !otherMails.includes(value);
      }, 'E-mail jest już używany'),
      check((value) => {
        return value !== accountEmail;
      }, 'Wprowadź inny e-mail lub zaznacz powyższe pole, jeśli jesteś właścicielem')
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateNewEmail = (value: string) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij e-mail'),
      nonEmpty('Uzupełnij e-mail'),
      email('Podaj prawidłowy adres e-mail')
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validatePesel = (value: string, otherPesel?: string[]) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij PESEL'),
      nonEmpty('Uzupełnij PESEL'),
      minLength(11, 'Uzupełnij PESEL'),
      maxLength(11, 'PESEL powinien mieć 11 cyfr'),
      check((value) => {
        return peselValidator(value);
      }, 'Podaj prawidłowy PESEL'),
      check((value) => {
        return otherPesel ? !otherPesel.includes(value) : true;
      }, 'PESEL jest już używany')
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateAddress = (value: EnumLike) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij adres'),
      nonEmpty('Uzupełnij adres'),
      minLength(3, 'Uzupełnij adres')
    ),
    value?.label
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateBuildingNumber = (value: string) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij nr budynku'),
      nonEmpty('Uzupełnij nr budynku'),
      regex(BUILDING_NUMBER_REGEX, 'Nr budynku jest niepoprawny')
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const isValidZipCode = (value: string | null | undefined) =>
  /^\d{2}-?\d{3}$/.test(value ?? '');

export const validateZipCode = (value: string) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij'),
      nonEmpty('Uzupełnij'),
      check(
        (value: string) => isValidZipCode(value),
        'Kod pocztowy jest niepoprawny'
      )
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateCity = (value: EnumLike) => {
  const result = safeParse(
    pipe(string('Uzupełnij miejscowość'), nonEmpty('Uzupełnij miejscowość')),
    value?.label
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateTaxOffice = (value: string) => {
  const result = safeParse(
    pipe(string('Wskaż Urząd Skarbowy'), nonEmpty('Wskaż Urząd Skarbowy')),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateNip = (value: string) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij NIP'),
      nonEmpty('Uzupełnij NIP'),
      minLength(10, 'Uzupełnij NIP'),
      custom(validatorNip(value), 'Podaj prawidłowy NIP')
    ),
    value
  );

  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateKrs = (value: string) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij KRS'),
      nonEmpty('Uzupełnij KRS'),
      minLength(10, 'Uzupełnij KRS')
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};

export const validateREGON = (value: string) => {
  const result = safeParse(
    pipe(
      string('Uzupełnij REGON'),
      nonEmpty('Uzupełnij REGON'),
      check((value) => {
        return value.length === 9 || value.length === 14;
      }, 'Podaj prawidłowy REGON'),
      custom(validatorRegon(value), 'Podaj prawidłowy REGON')
    ),
    value
  );
  if (result.success) {
    return true;
  }
  return result.issues[0].message;
};
